/**
 * WizardStepCollectionInfo
 *
 * Step 1 of the launchpad creation wizard.
 * Collects collection name, slug, description, taxon, and social links.
 */

import React from 'react';
import { TextField } from '../../../ui/components/TextField';
import { TextArea } from '../../../ui/components/TextArea';
import { FeatherCheck } from '@subframe/core';

export type CollectionType = 'standard' | 'composable' | 'edition';

export interface CollectionInfoFormData {
  name: string;
  slug: string;
  description: string;
  taxon: string;
  websiteUrl: string;
  twitterHandle: string;
  discordUrl: string;
  collectionType: CollectionType;
}

export interface WizardStepCollectionInfoProps {
  value: CollectionInfoFormData;
  onChange: (data: Partial<CollectionInfoFormData>) => void;
  errors?: Partial<Record<keyof CollectionInfoFormData, string>>;
  isSlugChecking: boolean;
  isSlugAvailable: boolean | null;
  onSlugManualEdit: () => void;
}

export const WizardStepCollectionInfo: React.FC<WizardStepCollectionInfoProps> = ({
  value,
  onChange,
  errors = {},
  isSlugChecking,
  isSlugAvailable,
  onSlugManualEdit,
}) => {
  return (
    <div className="space-y-6">
      {/* Name */}
      <TextField
        label={
          <span>
            Collection Name <span className="text-error-600">*</span>
          </span>
        }
        helpText="The name of your NFT collection"
        error={!!errors.name}
      >
        <TextField.Input
          placeholder="My Awesome Collection"
          value={value.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </TextField>
      {errors.name && (
        <p className="text-caption font-caption text-error-600 -mt-4">{errors.name}</p>
      )}

      {/* Slug */}
      <TextField
        label={
          <span>
            URL Slug <span className="text-error-600">*</span>
          </span>
        }
        helpText={
          isSlugChecking ? (
            'Checking availability...'
          ) : isSlugAvailable === true ? (
            <span className="flex items-center gap-1 text-success-600">
              <FeatherCheck className="w-3 h-3" />
              This slug is available!
            </span>
          ) : isSlugAvailable === false ? (
            'This slug is already taken'
          ) : (
            'Only lowercase letters, numbers, and hyphens'
          )
        }
        error={isSlugAvailable === false || !!errors.slug}
      >
        <TextField.Input
          placeholder="my-awesome-collection"
          value={value.slug}
          onChange={(e) => {
            onSlugManualEdit();
            onChange({
              slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
            });
          }}
        />
      </TextField>
      {errors.slug && (
        <p className="text-caption font-caption text-error-600 -mt-4">{errors.slug}</p>
      )}

      {/* Description */}
      <TextArea
        label={
          <span>
            Description <span className="text-error-600">*</span>
          </span>
        }
        helpText="Describe what makes your collection unique"
      >
        <TextArea.Input
          placeholder="Tell people about your collection..."
          value={value.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange({ description: e.target.value })
          }
        />
      </TextArea>

      {/* Taxon */}
      <TextField
        label={
          <span>
            Taxon <span className="text-error-600">*</span>
          </span>
        }
        helpText={
          errors.taxon ? (
            <span className="text-error-600">{errors.taxon}</span>
          ) : (
            'XRPL NFT taxon number. This groups NFTs in your collection on-chain.'
          )
        }
        error={!!errors.taxon}
      >
        <TextField.Input
          type="number"
          min="0"
          max="2147483647"
          placeholder="e.g., 12345"
          value={value.taxon}
          onChange={(e) => onChange({ taxon: e.target.value })}
        />
      </TextField>

      {/* Social Links */}
      <div className="border-t border-neutral-border pt-6">
        <h3 className="text-body-bold font-body-bold text-default-font mb-4">
          Social Links (Optional)
        </h3>

        <div className="space-y-4">
          <TextField label="Website">
            <TextField.Input
              placeholder="https://mycollection.com"
              type="url"
              value={value.websiteUrl}
              onChange={(e) => onChange({ websiteUrl: e.target.value })}
            />
          </TextField>

          <TextField label="Twitter Handle">
            <TextField.Input
              placeholder="@mycollection"
              value={value.twitterHandle}
              onChange={(e) => onChange({ twitterHandle: e.target.value })}
            />
          </TextField>

          <TextField label="Discord">
            <TextField.Input
              placeholder="https://discord.gg/..."
              type="url"
              value={value.discordUrl}
              onChange={(e) => onChange({ discordUrl: e.target.value })}
            />
          </TextField>
        </div>
      </div>
    </div>
  );
};
